import type { AddProviderFormValues } from "@/components/Forms/AddEditProviderDialog.tsx";
import { useFetchCompaniesForAutocomplete } from "@/queries/useFetchCompaniesForAutocomplete.ts";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

export type Option = {
    id: string | null;
    name: string | null;
};

export type CompanyAddress = {
    companyName: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    phoneNumber?: string;
    phoneExtension?: string;
    faxNumber?: string;
    email?: string;
};

type Props = {
    form: UseFormReturn<AddProviderFormValues>;
};

const ProviderCompanyAutocomplete = ({ form }: Props) => {
    const [searchString, setSearchString] = useState("");
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const companyIdFromForm = form.watch("idCompanyID");
    const isQueryEnabled =
        searchString.length >= 2 || (companyIdFromForm !== null && companyIdFromForm !== undefined);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchCompaniesForAutocomplete({
        searchString: searchString,
        fieldIdValue: companyIdFromForm ?? null,
        isQueryEnabled: isQueryEnabled,
        subQueryKey: "provider-company-autocomplete",
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((party) => ({
                id: party.idUUID,
                name: party.name ?? "",
            }));
        }
        return [];
    }, [data]);

    useEffect(() => {
        if (companyIdFromForm && options.length > 0) {
            const foundOption = options.find((option) => option.id === companyIdFromForm);
            if (foundOption && foundOption.id !== selectedOption?.id) {
                setSelectedOption(foundOption);
            }
        } else if (!companyIdFromForm) {
            setSelectedOption(null);
        }
    }, [companyIdFromForm, options, selectedOption?.id]);

    const handleChange = (_event: React.SyntheticEvent, newValue: Option | null) => {
        setSelectedOption(newValue);
        form.setValue("idCompanyID", newValue?.id || "");

        if (newValue && data) {
            const selectedCompany = data.find((item) => item.idUUID === newValue.id);

            const primaryEmail = selectedCompany?.emailAddresses?.find(
                (email) => email.primary === "Yes",
            );
            const primaryPhone = selectedCompany?.phoneNumbers?.find(
                (phone) => phone.primary === "Yes",
            );
            const faxNumber = selectedCompany?.phoneNumbers?.find(
                (phone) => phone.phoneType === "Fax",
            );

            if (selectedCompany) {
                form.setValue("companyName", selectedCompany.name || "");
                form.setValue("address", selectedCompany.address || "");
                form.setValue("address2", selectedCompany.address2 || "");
                form.setValue("city", selectedCompany.city || "");
                form.setValue("state", selectedCompany.state || "");
                form.setValue("zip", selectedCompany.zip || "");
                form.setValue("phoneNumber", primaryPhone?.phoneNumber || "");
                form.setValue("phoneExtension", primaryPhone?.extension || "");
                form.setValue("faxNumber", faxNumber?.phoneNumber || "");
                form.setValue("email", primaryEmail?.emailAddress || "");
            }
        } else {
            form.setValue("companyName", "");
            form.setValue("address", "");
            form.setValue("address2", "");
            form.setValue("city", "");
            form.setValue("state", "");
            form.setValue("zip", "");
            form.setValue("phoneNumber", "");
            form.setValue("phoneExtension", "");
            form.setValue("faxNumber", "");
            form.setValue("email", "");
        }
    };

    const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 2) {
            setSearchString(newInputValue);
        }
    };

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading companies</Typography>}
            <Autocomplete
                value={selectedOption}
                onChange={handleChange}
                onInputChange={handleInputChange}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name || ""}
                getOptionKey={(option: Option) => option.id || ""}
                isOptionEqualToValue={(option: Option, value: Option) => option.id === value.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Company Name *"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading || isRefetching ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
        </>
    );
};

export default ProviderCompanyAutocomplete;
